import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../index';
import {
  DateFilterModel,
  DateFilterPayload,
  DateFilterTab,
  DateFilterVisualDataPayload,
  DateFilterVisualTypePayload,
  SetAndApplyDateFilter,
} from '../../models/date-filter.model';
import { DatasetID } from 'common/models/filter.model';
import {
  convertToKwelloDate,
  DATE_FILTER_INITIAL_VALUE,
  DEFAULT_END_DATE,
  DEFAULT_PLANNING_END_DATE,
  DEFAULT_PLANNING_START_DATE,
  DEFAULT_START_DATE,
} from 'common/constants';

// identifier
export const DATE_SLICE_ID = 'dateFilter';

// default state
const initialState: DateFilterModel = {};

//slice
const dateSlice = createSlice({
  name: DATE_SLICE_ID,
  initialState,
  reducers: {
    setStartDate: (state: DateFilterModel, action: PayloadAction<DateFilterPayload>): void => {
      const { datasetId, date } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      if (date) {
        state[datasetId].tempStartDate = convertToKwelloDate(date);
      }
    },
    setEndDate: (state: DateFilterModel, action: PayloadAction<DateFilterPayload>): void => {
      const { datasetId, date } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      if (date) {
        state[datasetId].tempEndDate = convertToKwelloDate(date);
      }
    },
    setPlanningStartDate: (
      state: DateFilterModel,
      action: PayloadAction<DateFilterPayload>
    ): void => {
      const { datasetId, date } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      if (date) {
        state[datasetId].tempPlanningStartDate = convertToKwelloDate(date);
      }
    },
    setPlanningEndDate: (
      state: DateFilterModel,
      action: PayloadAction<DateFilterPayload>
    ): void => {
      const { datasetId, date } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      if (date) {
        state[datasetId].tempPlanningEndDate = convertToKwelloDate(date);
      }
    },
    setVisualType: (
      state: DateFilterModel,
      action: PayloadAction<DateFilterVisualTypePayload>
    ): void => {
      const { datasetId, visualType } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      if (visualType === DateFilterTab.LAST) {
        state[datasetId].tempVisualData = [6, 'Month(s)'];
        state[datasetId].tempStartDate = DEFAULT_START_DATE;
        state[datasetId].tempEndDate = DEFAULT_END_DATE;
      }

      state[datasetId] = { ...state[datasetId], tempVisualType: visualType };
    },
    setPlanningVisualType: (
      state: DateFilterModel,
      action: PayloadAction<DateFilterVisualTypePayload>
    ): void => {
      const { datasetId, visualType } = action.payload;

      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }

      if (visualType === DateFilterTab.NEXT) {
        state[datasetId].tempPlanningVisualData = [3, 'Month(s)'];
        state[datasetId].tempStartDate = DEFAULT_PLANNING_START_DATE;
        state[datasetId].tempEndDate = DEFAULT_PLANNING_END_DATE;
      }

      state[datasetId] = { ...state[datasetId], tempPlanningVisualType: visualType };
    },
    setVisualData: (
      state: DateFilterModel,
      action: PayloadAction<DateFilterVisualDataPayload>
    ): void => {
      const { datasetId, visualData } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      state[datasetId] = { ...state[datasetId], tempVisualData: visualData };
    },
    setPlanningVisualData: (
      state: DateFilterModel,
      action: PayloadAction<DateFilterVisualDataPayload>
    ): void => {
      const { datasetId, visualData } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      state[datasetId] = { ...state[datasetId], tempPlanningVisualData: visualData };
    },
    applyDateFilter: (state: DateFilterModel, action: PayloadAction<DatasetID>): void => {
      const { datasetId } = action.payload;

      state[datasetId] = {
        ...state[datasetId],
        startDate: state[datasetId].tempStartDate,
        endDate: state[datasetId].tempEndDate,
        visualData: state[datasetId].tempVisualData,
        visualType: state[datasetId].tempVisualType,
        planningStartDate: state[datasetId].tempPlanningStartDate,
        planningEndDate: state[datasetId].tempPlanningEndDate,
        planningVisualData: state[datasetId].tempPlanningVisualData,
        planningVisualType: state[datasetId].tempPlanningVisualType,
      };
    },
    setAndApplyDateFilter: (
      state: DateFilterModel,
      action: PayloadAction<SetAndApplyDateFilter>
    ): void => {
      const { datasetId, startDate, endDate, visualData, visualType } = action.payload;

      state[datasetId] = {
        ...state[datasetId],
        startDate:
          startDate instanceof Date && !isNaN(startDate.getTime())
            ? convertToKwelloDate(startDate)
            : '',
        endDate:
          endDate instanceof Date && !isNaN(endDate.getTime()) ? convertToKwelloDate(endDate) : '',
        visualData: visualData,
        visualType: visualType,
      };
    },
    rollbackDateFilter: (state: DateFilterModel, action: PayloadAction<DatasetID>): void => {
      const { datasetId } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = DATE_FILTER_INITIAL_VALUE;
      }
      state[datasetId].tempStartDate = state[datasetId].startDate;
      state[datasetId].tempEndDate = state[datasetId].endDate;
      state[datasetId].tempVisualData = state[datasetId].visualData;
      state[datasetId].tempVisualType = state[datasetId].visualType;
    },
    resetDateFilters: (state: DateFilterModel, action: PayloadAction<DatasetID>): void => {
      const { datasetId } = action.payload;
      state[datasetId] = DATE_FILTER_INITIAL_VALUE;
    },
  },
});

//selectors
export const selectDateFilter = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId];

    return {
      startDate: dataset?.startDate || DATE_FILTER_INITIAL_VALUE.startDate,
      endDate: dataset?.endDate || DATE_FILTER_INITIAL_VALUE.endDate,
      visualType: dataset?.visualType || DATE_FILTER_INITIAL_VALUE.visualType,
      visualData: dataset?.visualData || DATE_FILTER_INITIAL_VALUE.visualData,
    };
  }
);

export const selectTempDateFilter = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId];

    return {
      startDate: dataset?.tempStartDate || DATE_FILTER_INITIAL_VALUE.startDate,
      endDate: dataset?.tempEndDate || DATE_FILTER_INITIAL_VALUE.endDate,
      visualType: dataset?.tempVisualType || DATE_FILTER_INITIAL_VALUE.visualType,
      visualData: dataset?.tempVisualData || DATE_FILTER_INITIAL_VALUE.visualData,
    };
  }
);

export const selectTempPlanningDateFilter = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId];
    return {
      startDate: dataset?.tempPlanningStartDate || DATE_FILTER_INITIAL_VALUE.planningStartDate,
      endDate: dataset?.tempPlanningEndDate || DATE_FILTER_INITIAL_VALUE.planningEndDate,
      visualType: dataset?.tempPlanningVisualType || DATE_FILTER_INITIAL_VALUE.planningVisualType,
      visualData: dataset?.tempPlanningVisualData || DATE_FILTER_INITIAL_VALUE.planningVisualData,
    };
  }
);

export const selectStartDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.startDate;
  }
);

export const selectEndDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.endDate;
  }
);

export const selectVisualType = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;

    return dataset.visualType;
  }
);

export const selectVisualData = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.visualData;
  }
);

export const selectTempStartDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempStartDate;
  }
);

export const selectTempEndDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempEndDate;
  }
);

export const selectTempVisualType = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempVisualType;
  }
);

export const selectTempVisualData = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempVisualData;
  }
);

export const selectPlanningStartDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.planningStartDate;
  }
);

export const selectPlanningEndDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.planningEndDate;
  }
);

export const selectPlanningVisualType = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.planningVisualType;
  }
);

export const selectPlanningVisualData = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.planningVisualData;
  }
);

export const selectTempPlanningStartDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempPlanningStartDate;
  }
);

export const selectTempPlanningEndDate = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempPlanningEndDate;
  }
);

export const selectTempPlanningVisualType = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempPlanningVisualType;
  }
);

export const selectTempPlanningVisualData = createSelector(
  [(state: RootState) => state.dateFilter, (state: RootState, datasetId: string) => datasetId],
  (dateFilter, datasetId) => {
    const dataset = dateFilter[datasetId] || DATE_FILTER_INITIAL_VALUE;
    return dataset.tempPlanningVisualData;
  }
);

//actions
export const {
  setStartDate,
  setEndDate,
  setVisualType,
  setVisualData,
  applyDateFilter,
  setAndApplyDateFilter,
  rollbackDateFilter,
  resetDateFilters,
  setPlanningVisualType,
  setPlanningStartDate,
  setPlanningEndDate,
  setPlanningVisualData,
} = dateSlice.actions;

//reducer
export default dateSlice;
